import Container from '~/components/Container';
import Button from '~/components/Button';
import Reveal from '~/components/Reveal';
import PlanCard from '~/components/PlanCard';
import ProvidedBy from '~/components/ProvidedBy';
import { FEATURES } from '~/data/features';
import { PLANS } from '~/data/plans';
import { PLATFORMS } from '~/data/site';
import { useTitle } from '~/lib/useTitle';
import { motion, type Variants } from 'motion/react';
import s from './Home.module.scss';

const META_SAMPLE = `{
  "id": "tt0903747",
  "name": "Breaking Bad",
  "type": "series",
  "releaseInfo": "2008–2013",
  "genres": ["Crime", "Drama", "Thriller"],
  "runtime": "49 min"
}`;

const SEARCH_SAMPLE = `[
  { "id": "tt15239678", "name": "Dune: Part Two", "releaseInfo": "2024" },
  { "id": "tt1160419",  "name": "Dune",           "releaseInfo": "2021" }
]`;

const heroStagger: Variants = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.09, delayChildren: 0.05 } },
};

const heroItem: Variants = {
  hidden: { opacity: 0, y: 22 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.65, ease: [0.22, 1, 0.36, 1] } },
};

function CodeCard({ method, sample }: { method: string; sample: string }) {
  return (
    <div className={s.codeCard}>
      <div className={s.codeBar}>
        <i /><i /><i />
        <span>{method}</span>
      </div>
      <pre className={s.code}>{sample}</pre>
    </div>
  );
}

export default function Home() {
  useTitle('MetaHub — The metadata API for video apps');

  return (
    <>
      <section className={s.hero}>
        <div className={s.orbs} aria-hidden="true">
          <span />
          <span />
        </div>
        <Container className={s.heroInner}>
          <motion.div className={s.heroCopy} variants={heroStagger} initial="hidden" animate="visible">
            <motion.div className={s.eyebrow} variants={heroItem}>
              Metadata API
            </motion.div>
            <motion.h1 className={s.title} variants={heroItem}>
              The metadata layer for <span>video apps</span>.
            </motion.h1>
            <motion.p className={s.sub} variants={heroItem}>
              The most comprehensive API for movies, TV shows and series — titles, cast, ratings and
              synopses. One integration for every platform.
            </motion.p>
            <motion.div className={s.actions} variants={heroItem}>
              <Button to="/pricing" size="lg">
                View pricing
              </Button>
              <Button to="/docs" size="lg" variant="ghost">
                Read the docs
              </Button>
            </motion.div>
            <motion.div className={s.platforms} variants={heroItem}>
              {PLATFORMS.map((p) => (
                <span key={p}>{p}</span>
              ))}
            </motion.div>
          </motion.div>

          <motion.div
            className={s.heroCardWrap}
            initial={{ opacity: 0, y: 24, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{ duration: 0.8, delay: 0.15, ease: [0.22, 1, 0.36, 1] }}
          >
            <CodeCard method="GET  /meta/series/tt0903747.json" sample={META_SAMPLE} />
          </motion.div>
        </Container>
      </section>

      <section className={s.features}>
        <Container>
          <Reveal>
            <div className={s.sectionHead}>
              <div className={s.kicker}>Why MetaHub</div>
              <h2>Everything your catalog needs.</h2>
            </div>
          </Reveal>
          <div className={s.grid}>
            {FEATURES.map((f, i) => (
              <Reveal key={f.title} delay={(i % 3) * 90}>
                <div className={s.feature}>
                  <h3>{f.title}</h3>
                  <p>{f.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </Container>
      </section>

      <section className={s.showcase}>
        <Container className={s.showcaseInner}>
          <Reveal className={s.showcaseCopy}>
            <div className={s.kicker}>Developer-first</div>
            <h2>A clean REST API you can try right now.</h2>
            <p>
              Look up titles, search the catalog and fetch full metadata with simple GET requests.
              No SDK required — try the live playground in the docs.
            </p>
            <Button to="/docs">Open the playground</Button>
          </Reveal>
          <Reveal className={s.showcaseCardWrap} delay={120}>
            <CodeCard method="GET  /api/search?q=dune" sample={SEARCH_SAMPLE} />
          </Reveal>
        </Container>
      </section>

      <section className={s.pricing} id="pricing">
        <Container>
          <Reveal>
            <div className={s.sectionHead}>
              <div className={s.kicker}>Pricing</div>
              <h2>Simple plans that scale with you.</h2>
              <p className={s.sectionSub}>Start monthly, commit yearly, or pay once for life.</p>
            </div>
          </Reveal>
          <div className={s.plans}>
            {PLANS.map((p, i) => (
              <Reveal key={p.id} delay={i * 90}>
                <PlanCard plan={p} />
              </Reveal>
            ))}
          </div>
          <ProvidedBy />
          <div className={s.pricingMore}>
            <Button to="/pricing" variant="ghost">
              Compare all features
            </Button>
          </div>
        </Container>
      </section>

      <section className={s.cta}>
        <Container>
          <Reveal>
            <div className={s.ctaInner}>
              <h2>Start building with MetaHub.</h2>
              <p>Plug the most complete metadata API into your app today.</p>
              <div className={s.actions}>
                <Button to="/pricing" size="lg">
                  Get API access
                </Button>
                <Button to="/docs" size="lg" variant="ghost">
                  Read the docs
                </Button>
              </div>
            </div>
          </Reveal>
        </Container>
      </section>
    </>
  );
}
